import styled, { keyframes } from "styled-components";
import { useAdmin } from "../../../context/admin";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { CiEdit } from "react-icons/ci";
import { useEffect, useState, useMemo } from "react";
import AdminService from "../../../services/admin.service";
import { Payment, Traspass } from "../../../types/common.types";
import removeTimezone from "../../../utils/removeTimezone";
import Checkbox from "../../common/Checkbox";
import { MdOutlinePayments, MdAttachMoney, MdRateReview, MdOutlineFileDownload } from "react-icons/md";
import generateAccountStatementPdf from "../../../utils/generateAccountStatementPdf";
import { FaTrash } from "react-icons/fa6";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { add, format, startOfToday } from "date-fns";
import CalendarDual from "../../common/CalendarDual";
import { useGlobal } from "../../../context/global";

interface ReTraspaso {
    id: string
    tour_id: string
    tour_date: string
    origin_agency_id: string
    dest_agency_id: string
    traspass_price_in: number
    tickets_in: number
    traspass_price_out: number
    tickets_out: number
    passenger_name: string
    passenger_nationality?: string
    notes?: string
    origin_agency: { id: string; name: string; email: string }
    dest_agency: { id: string; name: string; email: string }
    payments: Payment[]
}

export default function AgencyRelationship() {
    const { setIsLoading } = useGlobal()
    const {
        relationshipAgencyId, setRelationshipAgencyId, user,
        setAddPaymentData, setViewPaymentAgencyId,
        setViewPaymentTraspassData, setCreatePaymentReTraspasoData,
        setCreateRevisionData, relationshipRefreshKey, setEditTraspassAmountReservationId
    } = useAdmin()
    const [data, setData] = useState<Traspass[]>([])
    const [payments, setPayments] = useState<Payment[]>([])
    const [reTraspasos, setReTraspasos] = useState<ReTraspaso[]>([])
    const [confirmDeleteReTraspasoId, setConfirmDeleteReTraspasoId] = useState<string | undefined>()
    const [day, setDay] = useState<Date>(startOfToday())
    const [endDay, setEndDay] = useState<Date | undefined>()
    const [showAll, setShowAll] = useState<boolean>(true)
    const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false)

    useEffect(() => {
        async function getInfo() {
            setIsLoading(true)
            try {
                if (!relationshipAgencyId) return
                const from = showAll ? undefined : format(day, 'yyyy-MM-dd')
                const to = showAll ? undefined : (endDay ? format(endDay, 'yyyy-MM-dd') : format(day, 'yyyy-MM-dd'))
                const [traspassRes, reRes] = await Promise.all([
                    AdminService.getTraspass(relationshipAgencyId, from, to),
                    AdminService.listReTraspasosByAgency(relationshipAgencyId, from, to)
                ])
                setData(traspassRes.data.reservations)
                setPayments(traspassRes.data.payments)
                setReTraspasos(reRes.data)
            } catch (error) {
                console.log(error)
            } finally {
                setIsLoading(false)
            }
        }
        getInfo()
    }, [relationshipAgencyId, relationshipRefreshKey, day, endDay, showAll])

    // Re-traspasos where this agency is ORIGIN → they owe us (positive side)
    const reTraspasos_positive = reTraspasos.filter(rt => rt.origin_agency_id === relationshipAgencyId)
    // Re-traspasos where this agency is DEST → we owe them (negative side)
    const reTraspasos_negative = reTraspasos.filter(rt => rt.dest_agency_id === relationshipAgencyId)

    const balance_positive = useMemo(() => {
        const from_reservations = data
            .filter(item => item.balance === 'positive')
            .reduce((acc, item) => acc + item.traspass_price + item.tickets, 0)
        const from_retraspasos = reTraspasos_positive
            .reduce((acc, rt) => acc + rt.traspass_price_in + rt.tickets_in, 0)
        return from_reservations + from_retraspasos
    }, [data, reTraspasos_positive])

    const balance_negative = useMemo(() => {
        const from_reservations = data
            .filter(item => item.balance === 'negative')
            .reduce((acc, item) => acc + item.traspass_price + item.tickets, 0)
        const from_retraspasos = reTraspasos_negative
            .reduce((acc, rt) => acc + rt.traspass_price_out + rt.tickets_out, 0)
        return from_reservations + from_retraspasos
    }, [data, reTraspasos_negative])

    const payments_positive = useMemo(() => {
        const tour = payments.filter(p => p.owner !== user?.user.agency_id).map(p => p.amount).reduce((a: number, b: number) => a + b, 0)
        const tickets = payments.filter(p => p.owner !== user?.user.agency_id).map(p => p.amount_tickets).reduce((a: number, b: number) => a + b, 0)
        return tour + tickets
    }, [payments, user])

    const payments_negative = useMemo(() => {
        const tour = payments.filter(p => p.owner === user?.user.agency_id).map(p => p.amount).reduce((a: number, b: number) => a + b, 0)
        const tickets = payments.filter(p => p.owner === user?.user.agency_id).map(p => p.amount_tickets).reduce((a: number, b: number) => a + b, 0)
        return tour + tickets
    }, [payments, user])

    const counterpartAgencyId = data[0]
        ? (data[0].agency.id !== user?.user.agency_id ? data[0].agency.id : data[0].agency_trip?.id)
        : relationshipAgencyId

    const [isDownloadingStatement, setIsDownloadingStatement] = useState<boolean>(false)

    async function downloadStatement() {
        if (!counterpartAgencyId) return
        setIsDownloadingStatement(true)
        try {
            const from = showAll ? undefined : format(day, 'yyyy-MM-dd')
            const to = showAll ? undefined : (endDay ? format(endDay, 'yyyy-MM-dd') : format(day, 'yyyy-MM-dd'))
            const response = await AdminService.getAccountStatement(counterpartAgencyId, from, to)
            await generateAccountStatementPdf(response.data)
        } catch (error) {
            console.log(error)
        } finally {
            setIsDownloadingStatement(false)
        }
    }

    async function deleteReTraspaso(id: string) {
        setIsLoading(true)
        try {
            await AdminService.deleteReTraspaso(id)
            setReTraspasos(prev => prev.filter(rt => rt.id !== id))
            setConfirmDeleteReTraspasoId(undefined)
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    function getReTraspasoPaid(rt: ReTraspaso, field: 'amount' | 'amount_tickets') {
        return rt.payments?.reduce((acc, p) => acc + (Number(p[field]) || 0), 0) || 0
    }

    function renderReTraspasoPrices(rt: ReTraspaso, isPositive: boolean) {
        const price = isPositive ? rt.traspass_price_in : rt.traspass_price_out
        const tickets = isPositive ? rt.tickets_in : rt.tickets_out
        const paid_tour = getReTraspasoPaid(rt, 'amount')
        const paid_tickets = getReTraspasoPaid(rt, 'amount_tickets')
        const deuda = price + tickets - paid_tour - paid_tickets
        return { price, tickets, paid_tour, paid_tickets, deuda }
    }

    return (
        <Main>
            <Modal>
                <div className="close" onClick={() => setRelationshipAgencyId(undefined)}>
                    <AiOutlineCloseCircle/>
                </div>

                <div className="top-actions">
                    <button className="view-payments" onClick={() => setViewPaymentAgencyId(counterpartAgencyId)}>
                        Ver pagos
                    </button>
                    <button className="download-statement" type="button" disabled={isDownloadingStatement} onClick={downloadStatement}>
                        <MdOutlineFileDownload/>
                        {isDownloadingStatement ? 'Generando...' : 'Descargar estado de cuenta'}
                    </button>
                </div>

                <DateFilter>
                    {!showAll && (
                        <div className="picker">
                            <IoIosArrowBack onClick={() => setDay(d => add(d, { days: -1 }))} />
                            <span onClick={() => setIsCalendarOpen(p => !p)}>
                                {format(day, 'dd/MM/yyyy')}{endDay ? ' - ' + format(endDay, 'dd/MM/yyyy') : ''}
                            </span>
                            <IoIosArrowForward onClick={() => setDay(d => add(d, { days: 1 }))} />
                        </div>
                    )}
                    {isCalendarOpen && !showAll &&
                        <CalendarDual
                            setSelectedDay={(d: Date) => setDay(d)}
                            selectPastDays
                            selectedDay={day}
                            endDay={endDay}
                            setEndDay={setEndDay}
                        />
                    }
                    <ShowAllBtn
                        type="button"
                        active={showAll}
                        onClick={() => { setShowAll(p => !p); setIsCalendarOpen(false) }}
                    >
                        {showAll ? 'Ver por fecha' : 'Ver todo'}
                    </ShowAllBtn>
                </DateFilter>

                <div className="header">
                    <span>
                        {data[0]?.agency.id !== user?.user.agency_id ? data[0]?.agency.name : data[0]?.agency_trip?.name || reTraspasos[0]?.origin_agency.name || reTraspasos[0]?.dest_agency.name}
                        <button className="positive" onClick={() => setAddPaymentData({ agency_id: counterpartAgencyId, type: 'send' })}>
                            $ {payments_negative}
                        </button>
                    </span>
                    <span>
                        {data[0]?.agency.id === user?.user.agency_id ? data[0]?.agency.name : data[0]?.agency_trip?.name || user?.user.name}
                        <button className="negative" onClick={() => setAddPaymentData({ agency_id: counterpartAgencyId, type: 'receive' })}>
                            $ {payments_positive}
                        </button>
                    </span>
                </div>

                <div className="account">
                    <span>Balance</span>
                    <div>
                        <span className="positive">$ {balance_positive}</span>
                        <span className="negative">$ {balance_negative}</span>
                    </div>
                    <span className="general">$ {(balance_positive - payments_positive) - (balance_negative - payments_negative)}</span>
                </div>

                {/* ── POSITIVE ────────────────────────────────────────── */}
                <h3>Positive</h3>
                {data.filter(r => r.balance === 'positive').map((reservation, idx) => (
                    <ReservationRow key={idx} reservation={reservation} 
                    setViewPaymentTraspassData={setViewPaymentTraspassData} setCreateRevisionData={setCreateRevisionData} 
                    setEditTraspassAmountReservationId={setEditTraspassAmountReservationId} counterpartAgencyId={counterpartAgencyId} />
                ))}
                {reTraspasos_positive.map((rt, idx) => {
                    const { price, tickets, paid_tour, paid_tickets, deuda } = renderReTraspasoPrices(rt, true)
                    return (
                        <div className="reservation retraspaso" key={`rp-${idx}`}>
                            <div className="header">
                                <div className="name">
                                    <span>↔ Re-traspaso · {rt.origin_agency.name} → {rt.dest_agency.name}</span>
                                </div>
                            </div>
                            <TableWrapper>
                                <Table>
                                    <thead>
                                        <tr>
                                            <th>Date</th><th>Passenger</th><th>Tour</th>
                                            <th>Traspass balance</th><th>Tickets</th>
                                            <th>Abono tour</th><th>Abono tickets</th>
                                            <th>Deuda</th><th>Pagado tour</th><th>Pagado tickets</th>
                                            <th style={{width:'60px'}}>Revisión</th>
                                            <th style={{width:'40px'}}></th>
                                            <th style={{width:'40px'}}></th>
                                            <th style={{width:'40px'}}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>{removeTimezone(rt.tour_date)}</td>
                                            <td>{rt.passenger_name}</td>
                                            <td>{rt.tour_id}</td>
                                            <td>{price}</td>
                                            <td>{tickets}</td>
                                            <td>-{paid_tour}</td>
                                            <td>-{paid_tickets}</td>
                                            <td>{deuda}</td>
                                            <td><Checkbox containerStyle={{display:'flex',justifyContent:'center'}} checked={paid_tour >= price}/></td>
                                            <td><Checkbox containerStyle={{display:'flex',justifyContent:'center'}} checked={paid_tickets >= tickets}/></td>
                                            <td><RevisionBadge status={(rt as any).revision?.status}/></td>
                                            <td>
                                                <button type="button" onClick={() => setViewPaymentTraspassData(rt.payments)}>
                                                    <MdOutlinePayments/>
                                                </button>
                                            </td>
                                            <td>
                                                <button type="button" className="add-payment" onClick={() => setCreatePaymentReTraspasoData({
                                                    re_traspaso_id: rt.id,
                                                    passenger_name: rt.passenger_name,
                                                    agency_id: rt.origin_agency_id,
                                                    type: 'receive',
                                                    payments: rt.payments
                                                })}>
                                                    <MdAttachMoney/>
                                                </button>
                                            </td>
                                            <td>
                                                <button type="button" onClick={() => setCreateRevisionData({
                                                    re_traspaso_id: rt.id,
                                                    agency_id: counterpartAgencyId || '',
                                                    passenger_name: rt.passenger_name,
                                                    tour_id: rt.tour_id,
                                                    date: removeTimezone(rt.tour_date),
                                                    current_status: (rt as any).revision?.status,
                                                    current_ticket_status: (rt as any).revision?.ticket_status,
                                                    current_notes: (rt as any).revision?.notes,
                                                    current_chat_source: (rt as any).revision?.chat_source,
                                                })}>
                                                    <MdRateReview/>
                                                </button>
                                            </td>
                                            <td>
                                                {confirmDeleteReTraspasoId === rt.id
                                                    ? <div style={{display:'flex', gap:'4px'}}>
                                                        <button type="button" className="confirm-delete" onClick={() => deleteReTraspaso(rt.id)}>✓</button>
                                                        <button type="button" onClick={() => setConfirmDeleteReTraspasoId(undefined)}>✕</button>
                                                      </div>
                                                    : <button type="button" className="delete" onClick={() => setConfirmDeleteReTraspasoId(rt.id)}>
                                                        <FaTrash/>
                                                      </button>
                                                }
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </TableWrapper>
                        </div>
                    )
                })}

                {/* ── NEGATIVE ────────────────────────────────────────── */}
                <h3>Negative</h3>
                {data.filter(r => r.balance === 'negative').map((reservation, idx) => (
                    <ReservationRow key={idx} reservation={reservation} 
                    setViewPaymentTraspassData={setViewPaymentTraspassData} setCreateRevisionData={setCreateRevisionData} 
                    setEditTraspassAmountReservationId={setEditTraspassAmountReservationId} counterpartAgencyId={counterpartAgencyId} negative />
                ))}
                {reTraspasos_negative.map((rt, idx) => {
                    const { price, tickets, paid_tour, paid_tickets, deuda } = renderReTraspasoPrices(rt, false)
                    return (
                        <div className="reservation retraspaso" key={`rn-${idx}`}>
                            <div className="header">
                                <div className="name">
                                    <span>↔ Re-traspaso · {rt.origin_agency.name} → {rt.dest_agency.name}</span>
                                </div>
                            </div>
                            <TableWrapper>
                                <Table>
                                    <thead>
                                        <tr>
                                            <th>Date</th><th>Passenger</th><th>Tour</th>
                                            <th>Traspass balance</th><th>Tickets</th>
                                            <th>Abono tour</th><th>Abono tickets</th>
                                            <th>Deuda</th><th>Pagado tour</th><th>Pagado tickets</th>
                                            <th style={{width:'60px'}}>Revisión</th>
                                            <th style={{width:'40px'}}></th>
                                            <th style={{width:'40px'}}></th>
                                            <th style={{width:'40px'}}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>{removeTimezone(rt.tour_date)}</td>
                                            <td>{rt.passenger_name}</td>
                                            <td>{rt.tour_id}</td>
                                            <td>-{price}</td>
                                            <td>-{tickets}</td>
                                            <td>{paid_tour}</td>
                                            <td>{paid_tickets}</td>
                                            <td>{deuda}</td>
                                            <td><Checkbox containerStyle={{display:'flex',justifyContent:'center'}} checked={paid_tour >= price}/></td>
                                            <td><Checkbox containerStyle={{display:'flex',justifyContent:'center'}} checked={paid_tickets >= tickets}/></td>
                                            <td><RevisionBadge status={(rt as any).revision?.status}/></td>
                                            <td>
                                                <button type="button" onClick={() => setViewPaymentTraspassData(rt.payments)}>
                                                    <MdOutlinePayments/>
                                                </button>
                                            </td>
                                            <td>
                                                <button type="button" className="add-payment" onClick={() => setCreatePaymentReTraspasoData({
                                                    re_traspaso_id: rt.id,
                                                    passenger_name: rt.passenger_name,
                                                    agency_id: rt.dest_agency_id,
                                                    type: 'send',
                                                    payments: rt.payments
                                                })}>
                                                    <MdAttachMoney/>
                                                </button>
                                            </td>
                                            <td>
                                                <button type="button" onClick={() => setCreateRevisionData({
                                                    re_traspaso_id: rt.id,
                                                    agency_id: counterpartAgencyId || '',
                                                    passenger_name: rt.passenger_name,
                                                    tour_id: rt.tour_id,
                                                    date: removeTimezone(rt.tour_date),
                                                    current_status: (rt as any).revision?.status,
                                                    current_ticket_status: (rt as any).revision?.ticket_status,
                                                    current_notes: (rt as any).revision?.notes,
                                                    current_chat_source: (rt as any).revision?.chat_source,
                                                })}>
                                                    <MdRateReview/>
                                                </button>
                                            </td>
                                            <td>
                                                {confirmDeleteReTraspasoId === rt.id
                                                    ? <div style={{display:'flex', gap:'4px'}}>
                                                        <button type="button" className="confirm-delete" onClick={() => deleteReTraspaso(rt.id)}>✓</button>
                                                        <button type="button" onClick={() => setConfirmDeleteReTraspasoId(undefined)}>✕</button>
                                                      </div>
                                                    : <button type="button" className="delete" onClick={() => setConfirmDeleteReTraspasoId(rt.id)}>
                                                        <FaTrash/>
                                                      </button>
                                                }
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </TableWrapper>
                        </div>
                    )
                })}
            </Modal>
        </Main>
    )
}

// ─── Revision status badge ────────────────────────────────────────────────
const REVISION_COLORS: Record<string, string> = {
    verified:       '#1e6b3a',
    no_chat_backup: '#7B6B00',
    error:          '#c0392b',
    pending:        '#888888',
}

function RevisionBadge({ status }: { status?: string }) {
    if (!status || status === 'pending') return <span style={{fontSize:'11px', color:'#aaa'}}>—</span>
    return (
        <span style={{
            fontSize: '10px', fontWeight: 600, padding: '2px 6px',
            borderRadius: '10px', color: '#fff',
            backgroundColor: REVISION_COLORS[status] || '#888'
        }}>
            {status === 'verified' ? '✓ ok' : status === 'error' ? '✗ error' : 'sin chat'}
        </span>
    )
}

// ─── Sub-component for regular reservation rows ────────────────────────────
function ReservationRow({ reservation, setViewPaymentTraspassData, setEditTraspassAmountReservationId, setCreateRevisionData, counterpartAgencyId, negative = false }: {
    reservation: Traspass
    setViewPaymentTraspassData: (p: Payment[]) => void
    setEditTraspassAmountReservationId: (id: string | undefined) => void
    setCreateRevisionData: (d: any) => void
    counterpartAgencyId?: string
    negative?: boolean
}) {
    const price = negative ? `-${reservation.traspass_price}` : reservation.traspass_price
    const tickets = negative ? `-${reservation.tickets}` : reservation.tickets
    const paid_tour = reservation.payments?.reduce((acc, p) => acc + (p.amount || 0), 0)
    const paid_tickets = reservation.payments?.reduce((acc, p) => acc + (p.amount_tickets || 0), 0)
    const deuda = reservation.traspass_price + reservation.tickets
        - reservation.payments?.reduce((acc, p) => acc + (p.amount || 0) + (p.amount_tickets || 0), 0)

    return (
        <div className="reservation">
            <div className="header">
                <div className="name">
                    <span>{reservation.balance === 'positive' ? reservation.agency.name : reservation.agency_trip.name}</span>
                </div>
            </div>
            <TableWrapper>
                <Table>
                    <thead>
                        <tr>
                            <th>Date</th><th>Passenger</th><th>Tour</th>
                            <th>Traspass balance</th><th>Tickets</th>
                            <th>Abono tour</th><th>Abono tickets</th>
                                            <th>Deuda</th><th>Pagado tour</th><th>Pagado tickets</th>
                            <th style={{width:'60px'}}>Revisión</th>
                            <th style={{width:'40px'}}></th>
                            <th style={{width:'40px'}}></th>
                            <th style={{width:'40px'}}></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{removeTimezone(reservation.trip.trip_date?.toLocaleString() || reservation.reservation_date.toString())}</td>
                            <td>{reservation.passenger.name}</td>
                            <td>{reservation.tour_id}</td>
                            <td>{price}</td>
                            <td>{tickets}</td>
                            <td>{negative ? '' : '-'}{paid_tour}</td>
                            <td>{negative ? '' : '-'}{paid_tickets}</td>
                            <td>{deuda}</td>
                            <td><Checkbox containerStyle={{display:'flex',justifyContent:'center'}} checked={paid_tour >= reservation.traspass_price!}/></td>
                            <td><Checkbox containerStyle={{display:'flex',justifyContent:'center'}} checked={paid_tickets >= reservation.tickets!}/></td>
                            <td><RevisionBadge status={(reservation as any).revision?.status}/></td>
                            <td>
                                <button type="button" onClick={() => setEditTraspassAmountReservationId(reservation.id)}>
                                    <CiEdit/>
                                </button>
                            </td>
                            <td>
                                <button type="button" onClick={() => setViewPaymentTraspassData(reservation.payments)}>
                                    <MdOutlinePayments/>
                                </button>
                            </td>
                            <td>
                                <button type="button" onClick={() => setCreateRevisionData({
                                    reservation_id: reservation.id,
                                    agency_id: counterpartAgencyId,
                                    passenger_name: reservation.passenger.name,
                                    tour_id: reservation.tour_id,
                                    date: removeTimezone(reservation.trip?.trip_date?.toLocaleString() || reservation.reservation_date.toString()),
                                    current_status: (reservation as any).revision?.status,
                                    current_ticket_status: (reservation as any).revision?.ticket_status,
                                    current_notes: (reservation as any).revision?.notes,
                                    current_chat_source: (reservation as any).revision?.chat_source,
                                })}>
                                    <MdRateReview/>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </Table>
            </TableWrapper>
        </div>
    )
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const Main = styled.div`
z-index: 100;
position: fixed;
top: 0; left: 0;
width: 100vw; height: 100vh;
background-color: ${props => props.theme.blackTransparent};
`

const showUpModal = keyframes`
    from { top: 100%; }
    to   { top: 50%; }
`

const Modal = styled.div`
width: 80%;
max-height: 75%;
min-height: 400px;
overflow: hidden;
overflow-y: unset;
max-width: 1000px;
position: absolute;
top: 50%; left: 50%;
transform: translate(-50%, -50%);
background-color: ${props => props.theme.white};
box-shadow: 0 0 20px ${props => props.theme.blackTransparent};
padding: 20px;
padding-top: 50px;
animation: ${showUpModal} .3s ease forwards;

.top-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 20px;
}
.view-payments {
    padding: 10px 20px;
}
.download-statement {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 20px;
    border-radius: 5px;
    border: 1px solid ${p => p.theme.blueLight};
    background-color: transparent;
    color: ${p => p.theme.blueLight};
    font-size: 14px;
    cursor: pointer;
    transition: all .2s;
    svg { font-size: 18px; }
    &:hover:not(:disabled) { background-color: ${p => p.theme.blueLight}; color: #fff; }
    &:disabled { opacity: .6; cursor: default; }
}
h3 {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 10px;
}
.close {
    position: absolute;
    right: 20px; top: 20px;
    font-size: 22px;
    cursor: pointer;
}
> .header {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80%;
    max-width: 250px;
    margin: 0 auto;
    border: 1px solid ${props => props.theme.dark};
    border-radius: 5px;
    > span {
        padding: 5px 10px;
        flex: 1;
        text-align: center;
        display: block;
        color: ${props => props.theme.dark};
        button {
            display: block;
            margin: auto;
            padding: 5px 10px;
            border: none;
            cursor: pointer;
            color: ${props => props.theme.white};
        }
        button.positive { background-color: ${props => props.theme.lightGreen}; }
        button.negative { background-color: ${props => props.theme.lightRed}; }
    }
}
.account {
    margin: 20px 0;
    > span {
        padding: 5px 10px;
        text-align: center;
        display: block;
        color: ${props => props.theme.white};
        background-color: ${props => props.theme.blueLight};
        width: 100px;
        margin: auto;
    }
    > div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 80%;
        max-width: 250px;
        margin: 0 auto;
        border: 1px solid ${props => props.theme.dark};
        border-radius: 5px;
        > span {
            padding: 5px 10px;
            flex: 1;
            text-align: center;
            display: block;
            color: ${props => props.theme.white};
            &.general { background-color: ${props => props.theme.blueLight}; }
            &.positive { background-color: ${props => props.theme.lightGreen}; }
            &.negative { background-color: ${props => props.theme.red}; }
        }
    }
}
.reservation {
    background-color: ${props => props.theme.whiteBackground};
    border-radius: 10px;
    box-shadow: 0 3px 5px ${props => props.theme.blackTransparent};
    margin-bottom: 20px;
    .header {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 10px 10px 0 0;
        position: relative;
        height: 40px;
        gap: 10px;
        background-color: ${props => props.theme.black};
        color: ${props => props.theme.white};
        .name {
            flex: 1;
            text-align: center;
            display: flex;
            gap: 10px;
            justify-content: center;
        }
    }
    &.retraspaso .header {
        background-color: ${props => props.theme.blueLight};
    }
}
`

const DateFilter = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    margin: 12px 0;
    .picker {
        display: flex;
        align-items: center;
        gap: 10px;
        span {
            cursor: pointer;
            font-size: 14px;
            color: ${p => p.theme.blueDark};
            min-width: 160px;
            text-align: center;
        }
        svg { cursor: pointer; font-size: 18px; color: ${p => p.theme.blueLight}; }
    }
`

const ShowAllBtn = styled.button<{ active: boolean }>`
    padding: 5px 16px;
    border-radius: 20px;
    border: 1px solid ${p => p.theme.blueLight};
    background-color: ${p => p.active ? p.theme.blueLight : 'transparent'};
    color: ${p => p.active ? '#fff' : p.theme.blueLight};
    font-size: 12px;
    cursor: pointer;
    transition: all .2s;
    &:hover { background-color: ${p => p.theme.blueLight}; color: #fff; }
`

const TableWrapper = styled.div`
width: 100%;
height: 100%;
overflow: hidden;
overflow-x: unset;
`

const Table = styled.table`
border-collapse: collapse;
table-layout: fixed;
height: 100%;
position: relative;
width: 100%;
min-width: 400px;
overflow: hidden;
thead {
    width: 100%;
    background-color: #FFFFFF;
}
th {
    font-weight: 400;
    overflow: hidden;
    text-overflow: ellipsis;
}
td {
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    button {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: auto;
        width: 25px;
        height: 25px;
        border-radius: 5px;
        border: none;
        background-color: ${props => props.theme.blueLight};
        color: ${props => props.theme.white};
        cursor: pointer;
        &.delete { background-color: ${props => props.theme.lightRed}; }
        &.confirm-delete { background-color: ${props => props.theme.lightGreen}; }
    }
}
tr {
    height: 40px;
    width: 100%;
}
`
